import type { ReactNode } from "react"

import type { RowAction } from "@coval/ui"
import {
  Bell,
  CalendarClock,
  Eye,
  Link2,
  Pencil,
  Share,
  Trash2,
} from "lucide-react"

import type { CsvColumnDef as ColumnDef } from "@/utils/csv/tableCsvColumns"
import {
  formatFullTimestamp,
  formatRelativeTimestamp,
} from "@/utils/formatting/format-timestamp"

import {
  notebookScheduleLabel,
  notebookVisibilityLabel,
  type NotebookRow,
} from "../utils/notebookSeries"
import {
  notebookSlackSendLabel,
  type WorkspaceSlackChannel,
} from "../utils/notebookSlack"

const DATE_COLUMN_WIDTH = 160
const CREATOR_COLUMN_WIDTH = 200
const VISIBILITY_COLUMN_WIDTH = 170

export function buildNotebookColumns({
  renderNameEditor,
  resolveCreator,
  workspaceSlackChannels = null,
}: {
  /** The open rename field for a notebook, or null to show its title. */
  renderNameEditor: (notebook: NotebookRow) => ReactNode
  resolveCreator: (creator: string | null | undefined) => string
  /** Null while the workspace's Slack bindings are unknown. */
  workspaceSlackChannels?: WorkspaceSlackChannel[] | null
}): ColumnDef<NotebookRow>[] {
  return [
    {
      id: "title",
      csvValue: (row) => row.title,
      header: "Name",
      accessorKey: "title",
      width: 420,
      cell: (notebook) =>
        renderNameEditor(notebook) ?? (
          <span className="min-w-0 truncate font-medium text-coval-text">
            {notebook.title}
          </span>
        ),
    },
    {
      id: "schedule",
      header: "Schedule",
      width: 210,
      csvValue: notebookScheduleLabel,
      cell: (notebook) => {
        const slackLabel = notebookSlackSendLabel(
          notebook,
          workspaceSlackChannels,
        )
        return (
          <div className="text-coval-text-muted">
            <div>{notebookScheduleLabel(notebook)}</div>
            {notebook.kind === "series" && (
              <div className="text-xs">
                {notebook.editions?.length ?? 0}{" "}
                {notebook.historyPartial ? "available " : ""}
                {notebook.editions?.length === 1 ? "edition" : "editions"}
                {slackLabel ? ` · ${slackLabel}` : ""}
                {notebook.schedule?.last_run?.status === "failed"
                  ? " · Last run failed"
                  : ""}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: "visibility",
      header: "Visibility",
      width: VISIBILITY_COLUMN_WIDTH,
      csvValue: notebookVisibilityLabel,
      cell: (notebook) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {notebookVisibilityLabel(notebook)}
        </span>
      ),
    },
    {
      id: "created_at",
      csvValue: (row) => row.created_at,
      header: "Latest notebook",
      width: DATE_COLUMN_WIDTH,
      cell: (notebook) => (
        <span
          className="text-coval-text-muted"
          title={formatFullTimestamp(notebook.created_at)}
        >
          {notebook.kind === "series" &&
          !notebook.editions?.length &&
          !notebook.template
            ? "—"
            : formatRelativeTimestamp(notebook.created_at)}
        </span>
      ),
    },
    {
      id: "created_by",
      csvValue: (row) => resolveCreator(row.created_by),
      header: "Created by",
      width: CREATOR_COLUMN_WIDTH,
      cell: (notebook) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {resolveCreator(notebook.created_by)}
        </span>
      ),
    },
  ]
}

export function buildNotebookRowActions({
  open,
  generatedHref,
  hrefFor,
  onSchedule,
  onShare,
  onSlack,
  onRename,
  onDelete,
}: {
  open: (notebook: NotebookRow) => void
  generatedHref: (id: string) => string
  hrefFor: (id: string) => string
  onSchedule: (notebook: NotebookRow) => void
  onShare: (notebook: NotebookRow) => void
  onSlack: (notebook: NotebookRow) => void
  onRename: (notebook: NotebookRow) => void
  onDelete: (notebook: NotebookRow) => void
}): (row: NotebookRow) => RowAction<NotebookRow>[] {
  return (row: NotebookRow): RowAction<NotebookRow>[] =>
    row.kind === "series"
      ? [
          { label: "View editions", icon: <Eye />, onSelect: open },
          {
            label: "Schedule",
            icon: <CalendarClock />,
            onSelect: onSchedule,
          },
          { label: "Share", icon: <Share />, onSelect: onShare },
          { label: "Slack notifications", icon: <Bell />, onSelect: onSlack },
        ]
      : [
          { label: "Open", icon: <Eye />, onSelect: open },
          // A generated notebook is scheduled from its own page; the list
          // dialog can only edit a schedule that already exists.
          ...(row.kind === "chat" || row.schedule
            ? [
                {
                  label: "Schedule",
                  icon: <CalendarClock />,
                  onSelect: onSchedule,
                },
              ]
            : []),
          { label: "Share", icon: <Share />, onSelect: onShare },
          {
            label: "Rename",
            icon: <Pencil />,
            skipFocusReturn: true,
            onSelect: onRename,
          },
          {
            label: "Copy Link",
            icon: <Link2 />,
            onSelect: (notebook) => {
              void navigator.clipboard.writeText(
                new URL(
                  notebook.kind === "generated"
                    ? generatedHref(notebook.id)
                    : hrefFor(notebook.id),
                  window.location.origin,
                ).toString(),
              )
            },
          },
          ...(row.kind === "chat"
            ? [
                {
                  label: "Delete",
                  icon: <Trash2 />,
                  destructive: true,
                  onSelect: onDelete,
                },
              ]
            : []),
        ]
}
